import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { InputGroup } from '@/components/form/InputGroup'
import { PasswordRequirements } from '@/components/form/PasswordRequirements'
import LockIcon from '@/views/assets/lock.svg'
import { useCommon } from '@/core/useCommon'
import { cn } from '@/styles'
import { hasInvalidPasswordChar, PASSWORD_MAX_LENGTH } from '@/hooks/update-password/passwordRules'
import { useStrongPasswordForm } from '@/hooks/update-password/useStrongPasswordForm'

type StrongPasswordFormProps = {
  // 提交前是否顯示確認彈窗
  confirm?: boolean
  // 修改成功
  onSuccess?: () => void
  className?: string
}

// 登入密碼表單 (密碼強度規則)
const StrongPasswordForm = ({ confirm, onSuccess, className }: StrongPasswordFormProps) => {
  const { t } = useCommon()
  const {
    form, // 表單
    resetKey, // 重置 key
    newPassword, // 新密碼
    isNewPasswordStrong, // 新密碼是否滿足規則
    matchStatus, // 確認密碼比對狀態
    isDisabled, // 提交按鈕是否禁用
    onSubmit, // 提交
    onReset, // 重置
  } = useStrongPasswordForm({ confirm, onSuccess })

  // 新密碼聚焦中: 顯示規則面板
  const [isNewPasswordFocused, setIsNewPasswordFocused] = useState(false)
  const newPasswordField = form.register('newPassword')

  // 規則面板在新密碼失焦時收起，下方按鈕會往上位移導致 click 落空
  // 新密碼聚焦時，按下非輸入框的元素不搶焦點，等 click 完成後再收起
  const onFormMouseDown = (e: React.MouseEvent<HTMLFormElement>) => {
    if (isNewPasswordFocused && !(e.target instanceof HTMLInputElement)) e.preventDefault()
  }

  return (
    <form onSubmit={onSubmit} onMouseDown={onFormMouseDown} className={cn('w-full', className)}>
      {/* 重置時重新掛載，恢復密文顯示與初始狀態 */}
      <div key={resetKey} className="flex flex-col gap-12">
        {/* 舊密碼: 不做格式校驗 */}
        <div>
          <InputGroup Icon={LockIcon} label={t('Q027')} placeholder={t('Q028')} required type="password" {...form.register('oldPassword')} />
        </div>

        {/* 新密碼: 含不允許的字元時立即提示，未滿足規則時失焦後紅線 */}
        <div>
          <InputGroup
            Icon={LockIcon}
            label={t('old:newPassword')}
            placeholder={t('B532')}
            required
            type="password"
            maxLength={PASSWORD_MAX_LENGTH}
            errmsg={hasInvalidPasswordChar(newPassword) ? t('old:pwdInvalidChar') : undefined}
            invalid={!isNewPasswordFocused && !!newPassword && !isNewPasswordStrong}
            {...newPasswordField}
            onFocus={() => setIsNewPasswordFocused(true)}
            onBlur={(e) => {
              setIsNewPasswordFocused(false)
              newPasswordField.onBlur(e)
            }}
          />
        </div>

        {/* 密碼規則: 只在輸入新密碼時顯示 */}
        {isNewPasswordFocused && <PasswordRequirements value={newPassword} />}

        {/* 確認密碼: 即時比對 */}
        <div>
          <InputGroup
            Icon={LockIcon}
            label={t('B168')}
            placeholder={t('B532')}
            required
            type="password"
            maxLength={PASSWORD_MAX_LENGTH}
            errmsg={matchStatus === 'mismatch' ? t('old:pwdNotMatch') : undefined}
            {...form.register('confirmPassword')}
          />
          {matchStatus === 'match' && <div className="text-12 sm:text-16 mt-8 text-[#00C784] capitalize">{t('old:pwdMatch')}</div>}
        </div>
      </div>

      {/* 按鈕組 */}
      <div className="mt-24 flex gap-12">
        <Button type="button" variant="outlineBlue" className="text-16 h-36 flex-1 font-normal" onClick={onReset}>
          {t('P486')}
        </Button>
        <Button type="submit" className="text-16 h-36 flex-1 font-normal" disabled={isDisabled}>
          {t('P877')}
        </Button>
      </div>
    </form>
  )
}

export default StrongPasswordForm
